<script setup lang="ts">
import { DeviceStatusHubKey } from '@/components/deviceStatus/utils/severity';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore';
import { injectStrict } from '@/utils/injectStrict';
import { storeToRefs } from 'pinia';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import DeviceStatusIsland from './DeviceStatusIsland.vue';
import { liveItems, refreshLive, startLive, stopLive } from './deviceStatusIslandLive';

const deviceStatusHub = injectStrict(DeviceStatusHubKey);

const enabled = ref(false);

// Not while typing: text fields use Ctrl+I for italic.
function isEditableTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
  );
}

function onKeydown(event: KeyboardEvent) {
  if (
    event.ctrlKey &&
    !event.altKey &&
    !event.metaKey &&
    // Plain Ctrl+I only — Ctrl+Shift+I must keep opening the devtools.
    !event.shiftKey &&
    event.key.toLowerCase() === 'i' &&
    !isEditableTarget(event.target)
  ) {
    // Suppresses Firefox's native Ctrl+I (Page Info); no-op elsewhere.
    event.preventDefault();
    enabled.value = !enabled.value;
  }
}

watch(enabled, (on) => {
  if (on) {
    startLive(deviceStatusHub);
  } else {
    stopLive();
  }
});

const island = ref<InstanceType<typeof DeviceStatusIsland> | null>(null);
const route = useRoute();
watch(
  () => route.fullPath,
  () => island.value?.collapse(),
);

const { registrationPending, deRegistrationPending } = storeToRefs(useQaEquipmentStore());
watch([registrationPending, deRegistrationPending], (pending, wasPending) => {
  if (wasPending.some((was, index) => was && !pending[index])) {
    refreshLive();
  }
});

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
  stopLive();
});
</script>

<template>
  <DeviceStatusIsland v-if="enabled" ref="island" :items="liveItems" />
</template>
